import React from 'react';
import { CheckCircle, FileText, Inbox, MessageSquare, Phone, RotateCcw, Shuffle, Sliders, UserCheck } from 'lucide-react';
import { formatClinicDateTime } from '../../utils/clinicTime';
import { CHANNEL, CONTACT_OUTCOME, EVENT_LABEL, STATUS } from './complaintStatus';

// Everything that has happened to a complaint, oldest first. Append-only on the server, so this is
// a record rather than a view of current state: it is what a reader needs to answer "was this
// actually looked into?" months later.

const ICON = {
  RECEIVED: Inbox,
  ASSIGNED: UserCheck,
  UNASSIGNED: Shuffle,
  CONTACT: Phone,
  NOTE: FileText,
  STATUS_CHANGED: Shuffle,
  INFORMED: MessageSquare,
  TRIAGED: Sliders,
  REOPENED: RotateCcw,
  CLOSED: CheckCircle,
};

const statusLabel = (key) => STATUS[key]?.label || key;

/** The one-line summary of an event, before its note. */
const headline = (e) => {
  switch (e.type) {
    case 'CONTACT': {
      const outcome = CONTACT_OUTCOME[e.contactOutcome];
      const how = CHANNEL[e.channel] || e.channel || '';
      return outcome ? `${outcome.label}${how ? ` · ${how.toLowerCase()}` : ''}` : how || 'Contacted';
    }
    case 'STATUS_CHANGED':
      return e.fromStatus
        ? `${statusLabel(e.fromStatus)} → ${statusLabel(e.toStatus)}`
        : statusLabel(e.toStatus);
    case 'INFORMED':
      return `Told the complainant${e.channel ? ` · ${(CHANNEL[e.channel] || e.channel).toLowerCase()}` : ''}`;
    default:
      return EVENT_LABEL[e.type] || e.type;
  }
};

const TONE = {
  ok: 'text-emerald-700',
  warn: 'text-amber-700',
};

const ComplaintTimeline = ({ events = [] }) => {
  if (events.length === 0) {
    return <p className="text-sm text-gray-500">Nothing has happened to this complaint yet.</p>;
  }
  return (
    <ol className="space-y-3">
      {events.map((e, i) => {
        const Icon = ICON[e.type] || FileText;
        const tone = e.type === 'CONTACT' ? TONE[CONTACT_OUTCOME[e.contactOutcome]?.tone] : null;
        return (
          <li key={e.id || `${e.type}-${e.at}-${i}`} className="flex gap-3">
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                <Icon className="w-3.5 h-3.5 text-gray-500" aria-hidden="true" />
              </div>
              {/* The thread between entries, so a long history reads as one sequence. */}
              {i < events.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
            </div>
            <div className="min-w-0 pb-1">
              <p className={`text-sm font-medium ${tone || 'text-gray-900'}`}>{headline(e)}</p>
              {e.note && (
                <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap" dir="auto">
                  {e.note}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-0.5">
                {formatClinicDateTime(e.at)}
                {e.byName ? ` · ${e.byName}` : ''}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default ComplaintTimeline;
